import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { ReportService } from './ReportService'
import {
  ReportDto,
  PsychologyType,
  ReportsInitialStateType,
  TimeReportType,
  DurationReportType,
  RiskReportType,
  StrategiesType,
  InstrumentsType,
  DrawdownType,
  TradingConfluencesType,
} from './types'

export const fetchTimeReport = createAsyncThunk(
  'reports/fetchTimeReport',
  async (data: ReportDto) => {
    try {
      const { data: timeReportData } = await ReportService.getTimeReport(data)
      return timeReportData
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchDurationReport = createAsyncThunk(
  'reports/fetchDurationReport',
  async (data: ReportDto) => {
    try {
      const { data: durationReportData } = await ReportService.getDurationReport(data)
      return durationReportData
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchPsychologyReport = createAsyncThunk(
  'reports/fetchPsychologyReport',
  async (data: ReportDto) => {
    try {
      const { data: psychologyReportData } = await ReportService.getPsychologyReport(data)
      return psychologyReportData
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchRiskReport = createAsyncThunk(
  'reports/fetchRiskReport',
  async (data: ReportDto) => {
    try {
      const { data: riskReportData } = await ReportService.getRiskReport(data)
      return riskReportData
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchStrategyReport = createAsyncThunk(
  'reports/fetchStrategyReport',
  async (data: ReportDto) => {
    try {
      const { data: strategyReport } = await ReportService.getTradingStrategyReport(data)
      return strategyReport
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchTradingConfluenceReport = createAsyncThunk(
  'reports/fetchTradingConfluenceReport',
  async (data: ReportDto) => {
    try {
      const { data: reportData } = await ReportService.getTradingConfluenceReport(data)

      return reportData
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchInstrumentsReport = createAsyncThunk(
  'reports/fetchInstrumentsReport',
  async (data: ReportDto) => {
    try {
      const { data: instrumentsReport } = await ReportService.getInstrumentsReport(data)
      return instrumentsReport
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const fetchDrawdownReport = createAsyncThunk(
  'reports/fetchDrawdownReport',
  async (data: ReportDto) => {
    try {
      const { data: drawdownReport } = await ReportService.getDrawdownReport(data)
      return drawdownReport
    } catch (error) {
      throw Error(error.message)
    }
  },
)

const initialState: ReportsInitialStateType = {
  time: {} as TimeReportType,
  duration: {} as DurationReportType,
  psychology: {} as PsychologyType,
  risk: {} as RiskReportType,
  strategy: {} as StrategiesType,
  tradingConfluence: {} as TradingConfluencesType,
  instruments: {} as InstrumentsType,
  drawdown: {} as DrawdownType,
  reportsForPrint: [],
}

const ReportsSlice = createSlice({
  name: 'Reports',
  initialState,
  reducers: {
    setReportsForPrint(state, action) {
      state.reportsForPrint = action.payload
    },
    clearReports(state) {
      state.time = {} as TimeReportType
      state.duration = {} as DurationReportType
      state.psychology = {} as PsychologyType
      state.risk = {} as RiskReportType
      state.strategy = {} as StrategiesType
      state.tradingConfluence = {} as TradingConfluencesType
      state.instruments = {} as InstrumentsType
      state.reportsForPrint = []
    },
    updateCommentsStatus(state, action) {
      state.psychology.chartOfComments.isPositive = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTimeReport.fulfilled, (state, action) => {
      state.time = action.payload
    })
    builder.addCase(fetchDurationReport.fulfilled, (state, action) => {
      state.duration = action.payload
    })
    builder.addCase(fetchPsychologyReport.fulfilled, (state, action) => {
      state.psychology = {
        ...action.payload,
        chartOfComments: { ...action.payload.chartOfComments, isPositive: false },
      }
    })
    builder.addCase(fetchRiskReport.fulfilled, (state, action) => {
      state.risk = action.payload
    })
    builder.addCase(fetchStrategyReport.fulfilled, (state, action) => {
      state.strategy = action.payload
    })
    builder.addCase(fetchTradingConfluenceReport.fulfilled, (state, action) => {
      state.tradingConfluence = action.payload
    })
    builder.addCase(fetchInstrumentsReport.fulfilled, (state, action) => {
      state.instruments = action.payload
    })
    builder.addCase(fetchDrawdownReport.fulfilled, (state, action) => {
      state.drawdown = action.payload
    })
  },
})

const ReportsReducer = ReportsSlice.reducer

export const { setReportsForPrint, clearReports, updateCommentsStatus } = ReportsSlice.actions

export default ReportsReducer
